import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { getOpenStatus } from "@/utils/openStatus";

const LINKS = [
    { href: "#menu", label: "Menu & Tarifs", testid: "navbar-link-menu" },
    { href: "#manifesto", label: "L'Histoire", testid: "navbar-link-manifesto" },
    { href: "#location", label: "Notre Adresse", testid: "navbar-link-location" },
    { href: "#socials", label: "Nos Réseaux", testid: "navbar-link-socials" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            data-testid="navbar-container"
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
                scrolled ? "glass border-b border-white/10" : "border-b border-transparent"
            }`}
        >
            <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
                <a
                    href="#hero"
                    data-testid="navbar-logo"
                    className="flex items-center gap-3"
                >
                    <img
                        src="/assets/logo.png"
                        alt="Petit Veau Athis-Mons"
                        className="h-11 w-11 object-contain drop-shadow-[0_0_12px_rgba(185,28,28,0.35)]"
                    />
                    <span className="flex flex-col leading-none">
                        <span className="font-display text-lg font-black uppercase tracking-tight">
                            Petit Veau
                        </span>
                        <span className="font-script text-sm text-rose-500">Athis-Mons</span>
                    </span>
                </a>

                <div className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={l.testid}
                            className="font-mono2 text-xs uppercase tracking-widest text-zinc-400 transition-colors duration-300 hover:text-rose-400"
                        >
                            {l.label}
                        </a>
                    ))}
                </div>

                <div className="hidden items-center gap-4 lg:flex">
                    <span
                        data-testid="navbar-open-status"
                        className="font-mono2 flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-400"
                    >
                        <span
                            className={`animate-pulse-dot h-1.5 w-1.5 rounded-full ${
                                getOpenStatus().open ? "bg-emerald-400" : "bg-rose-500"
                            }`}
                        />
                        {getOpenStatus().label}
                    </span>
                    <motion.a
                        href="#menu"
                        data-testid="navbar-cta-button"
                        whileTap={{ scale: 0.96 }}
                        whileHover={{ scale: 1.03 }}
                        className="font-mono2 rounded-full bg-rose-600 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:bg-rose-500"
                    >
                        Voir la Carte
                    </motion.a>
                </div>

                <button
                    data-testid="navbar-mobile-toggle"
                    onClick={() => setOpen(!open)}
                    className="grid h-10 w-10 place-items-center rounded-md border border-white/10 text-zinc-200 lg:hidden"
                    aria-label="Menu"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="glass overflow-hidden border-b border-white/10 lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-5 py-4">
                            {LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    className="rounded-md px-3 py-3 text-sm font-semibold uppercase tracking-wide text-zinc-300 transition-colors hover:bg-white/5 hover:text-rose-400"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="#menu"
                                onClick={() => setOpen(false)}
                                className="font-mono2 mt-2 rounded-full bg-rose-600 px-5 py-3 text-center text-xs font-bold uppercase tracking-widest text-white"
                            >
                                Voir la Carte
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
